import { t } from "@/lib/i18n/interpolate";
import {
  asSentence,
  capitalizeFirst,
  stripFinalPeriod,
} from "@/lib/tool-kit/publishing/authors";
import { formatIsoDate } from "@/lib/tool-kit/publishing/dates";

/**
 * Ethics approval and consent statements.
 *
 * What goes in follows the journals' requirements: Nature Portfolio asks for the
 * approving committee's name and reference number, a statement that human research
 * followed the Declaration of Helsinki, the committee that granted any exemption, and a
 * statement that informed consent was obtained (or waived by the committee); for
 * animals, the approving institutional or licensing committee, with ARRIVE 2.0
 * recommended for reporting. PLOS adds that oral consent needs a reason, the
 * committee's approval, and a record of how it was documented.
 */

export type StudyType = "human" | "animal" | "exempt";
export type ExemptionBasis = "exempt" | "notRequired";
export type ConsentType =
  | "written"
  | "electronic"
  | "oral"
  | "implied"
  | "waived";

export interface EthicsInputs {
  study: StudyType;
  committee: string;
  reference: string;
  /** "YYYY-MM-DD" */
  date: string;
  helsinki: boolean;
  guidelines: string;
  basis: ExemptionBasis;
  exemptionReason: string;
  consent: ConsentType;
  oralReason: string;
  oralRecord: string;
  waiverReason: string;
  minors: boolean;
  publication: boolean;
  regulations: string;
  arrive: boolean;
}

export interface EthicsTemplates {
  approvalHuman: string;
  approvalAnimal: string;
  onDate: string;
  reference: string;
  protocol: string;
  helsinki: string;
  helsinkiAnd: string;
  guidelines: string;
  animalRegulations: string;
  arrive: string;
  exemptReviewed: string;
  notRequiredBy: string;
  notRequired: string;
  because: string;
  consentWritten: string;
  consentElectronic: string;
  consentOral: string;
  consentOralApproved: string;
  consentImplied: string;
  consentWaived: string;
  consentMinors: string;
  consentPublication: string;
  placeholderCommittee: string;
  placeholderReason: string;
  placeholderRecord: string;
}

export interface EthicsStatement {
  approval: string;
  consent: string;
  publication: string;
}

function clean(value: string): string {
  return stripFinalPeriod(value);
}

function committeeOf(inputs: EthicsInputs, templates: EthicsTemplates): string {
  const committee = clean(inputs.committee);
  return committee === "" ? templates.placeholderCommittee : committee;
}

function finish(sentences: readonly string[]): string {
  return sentences
    .filter((sentence) => sentence !== "")
    .map((sentence) => capitalizeFirst(asSentence(sentence)))
    .join(" ");
}

/** " on March 5, 2024 (reference number 2024-117)" — whichever parts are known. */
function dateAndReference(
  inputs: EthicsInputs,
  templates: EthicsTemplates,
  locale: string,
  referenceTemplate: string
): string {
  const date = formatIsoDate(inputs.date, locale);
  const reference = clean(inputs.reference);
  return `${date ? t(templates.onDate, { date }) : ""}${
    reference === "" ? "" : t(referenceTemplate, { reference })
  }`;
}

function conductSentence(
  inputs: EthicsInputs,
  templates: EthicsTemplates
): string {
  const guidelines = clean(inputs.guidelines);
  if (inputs.helsinki) {
    return guidelines === ""
      ? templates.helsinki
      : t(templates.helsinkiAnd, { guidelines });
  }
  return guidelines === "" ? "" : t(templates.guidelines, { guidelines });
}

function humanApproval(
  inputs: EthicsInputs,
  templates: EthicsTemplates,
  locale: string
): string {
  return finish([
    t(templates.approvalHuman, {
      committee: committeeOf(inputs, templates),
      details: dateAndReference(inputs, templates, locale, templates.reference),
    }),
    conductSentence(inputs, templates),
  ]);
}

function animalApproval(
  inputs: EthicsInputs,
  templates: EthicsTemplates,
  locale: string
): string {
  const regulations = clean(inputs.regulations);
  return finish([
    t(templates.approvalAnimal, {
      committee: committeeOf(inputs, templates),
      details: dateAndReference(inputs, templates, locale, templates.protocol),
    }),
    regulations === "" ? "" : t(templates.animalRegulations, { regulations }),
    inputs.arrive ? templates.arrive : "",
  ]);
}

function exemptApproval(
  inputs: EthicsInputs,
  templates: EthicsTemplates
): string {
  const reason = clean(inputs.exemptionReason);
  const because = reason === "" ? "" : t(templates.because, { reason });
  const reference = clean(inputs.reference);
  const referencePart =
    reference === "" ? "" : t(templates.reference, { reference });
  if (inputs.basis === "exempt") {
    return finish([
      t(templates.exemptReviewed, {
        committee: committeeOf(inputs, templates),
        because,
        reference: referencePart,
      }),
      conductSentence(inputs, templates),
    ]);
  }
  const committee = clean(inputs.committee);
  const decided =
    committee === ""
      ? t(templates.notRequired, { because })
      : t(templates.notRequiredBy, {
          because,
          committee,
          reference: referencePart,
        });
  return finish([decided, conductSentence(inputs, templates)]);
}

function consentSentences(
  inputs: EthicsInputs,
  templates: EthicsTemplates
): string[] {
  const committee = committeeOf(inputs, templates);
  switch (inputs.consent) {
    case "electronic":
      return [templates.consentElectronic];
    case "implied":
      return [templates.consentImplied];
    case "oral":
      return [
        t(templates.consentOral, {
          reason: clean(inputs.oralReason) || templates.placeholderReason,
        }),
        t(templates.consentOralApproved, {
          committee,
          record: clean(inputs.oralRecord) || templates.placeholderRecord,
        }),
      ];
    case "waived":
      return [
        t(templates.consentWaived, {
          committee,
          reason: clean(inputs.waiverReason) || templates.placeholderReason,
        }),
      ];
    default:
      return [templates.consentWritten];
  }
}

/** The statement's paragraphs; an empty string is a paragraph that does not apply. */
export function ethicsStatement(
  inputs: EthicsInputs,
  templates: EthicsTemplates,
  locale: string
): EthicsStatement {
  if (inputs.study === "animal") {
    return {
      approval: animalApproval(inputs, templates, locale),
      consent: "",
      publication: "",
    };
  }
  const approval =
    inputs.study === "human"
      ? humanApproval(inputs, templates, locale)
      : exemptApproval(inputs, templates);
  const consent = finish([
    ...consentSentences(inputs, templates),
    inputs.minors && inputs.consent !== "waived" ? templates.consentMinors : "",
  ]);
  return {
    approval,
    consent,
    publication: inputs.publication
      ? finish([templates.consentPublication])
      : "",
  };
}
